import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { uploadImageToS3, validateImageUpload, deleteFileFromS3 } from "@/services/s3Service";
import { ImagePlus } from "lucide-react";

export default function EditSlide() {
  const { id } = useParams();
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [currentStoragePath, setCurrentStoragePath] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSlide();
    // eslint-disable-next-line
  }, [id]);

  async function fetchSlide() {
    setLoading(true);
    const { data, error } = await supabase
      .from("home_slides")
      .select("title, storage_path")
      .eq("id", id)
      .single();
    if (error) {
      toast({ title: "Hata", description: "Slayt yüklenemedi.", variant: "destructive" });
    } else if (data) {
      setTitle(data.title || "");
      setCurrentStoragePath(data.storage_path || "");
      if (data.storage_path) {
        setPreviewUrl(data.storage_path);
      }
    }
    setLoading(false);
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      // Dosya validasyonu
      const validationError = validateImageUpload([selectedFile]);
      if (validationError) {
        toast({ title: "Hata", description: validationError, variant: "destructive" });
        return;
      }
      
      setFile(selectedFile);
      setPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!previewUrl) {
      toast({ title: "Hata", description: "Lütfen bir resim seçin.", variant: "destructive" });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let storage_path = currentStoragePath;
      
      // Eğer yeni bir dosya yüklendiyse
      if (file) {
        // Eski dosyayı sil (eğer varsa)
        if (currentStoragePath) {
          try {
            await deleteFileFromS3(currentStoragePath);
          } catch (deleteError) {
            console.error("Eski dosya silinemedi:", deleteError);
            // Silme hatası olsa bile devam et
          }
        }
        
        // Yeni dosyayı yükle
        storage_path = await uploadImageToS3(file, "slides");
      }
      
      // Supabase'i güncelle
      const { error } = await supabase
        .from("home_slides")
        .update({ title, storage_path })
        .eq("id", id);
        
      if (error) {
        toast({ title: "Hata", description: "Slayt güncellenemedi.", variant: "destructive" });
      } else {
        toast({ title: "Slayt güncellendi", description: "Slayt başarıyla güncellendi." });
        navigate("/content/home/slides");
      }
    } catch (error) {
      console.error("Güncelleme hatası:", error);
      toast({ title: "Hata", description: "Resim yüklenirken bir hata oluştu.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentLayout title="Slayt Düzenle" backLink="/content/home/slides">
      {loading ? (
        <div className="p-6">Yükleniyor...</div>
      ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-w-xl mx-auto">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Başlık</Label>
              <Input
                id="title"
                placeholder="Slayt başlığı"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Resim</Label>
              <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                ${previewUrl ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary"}`}>
                <input
                  type="file"
                  id="slide-image"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                
                {previewUrl ? (
                  <div className="relative group">
                    <img 
                      src={previewUrl} 
                      alt="Slayt resmi" 
                      className="max-h-64 mx-auto rounded-lg" 
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        document.getElementById("slide-image")?.click();
                      }}
                    >
                      Değiştir
                    </Button>
                  </div>
                ) : (
                  <>
                    <label htmlFor="slide-image" className="cursor-pointer">
                      <ImagePlus className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Slayt için resim yükleyin
                      </p>
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => document.getElementById("slide-image")?.click()}
                    >
                      Resim Seç
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/content/home/slides")}>İptal</Button>
            <Button type="submit" disabled={isSubmitting}>{isSubmitting ? "Kaydediliyor..." : "Kaydet"}</Button>
          </div>
        </form>
      )}
    </ContentLayout>
  );
}
